/**
 * The shapes the app's data layer speaks, independent of where they come from.
 *
 * These are the contracts the real analytics, insights and automation endpoints
 * will implement. Until they exist, `MockApiClient` produces them from seeded
 * fixtures for the demo build and `NotLiveApiClient` answers them empty for a
 * production one; every response says which in `meta.source`, which is what
 * the "Demo data" and "Not live yet" badges read — a screen never decides for
 * itself whether its numbers are real.
 *
 * As everywhere in this app, nothing here names a tenant: the server resolves
 * it from the session (blueprint 03 §3.2).
 */

import type { Polarity, ValueFormat } from '@/design/format'
import type { DailyMetrics, Environment, EnvironmentSelector } from '@/lib/api'
import type { DrillDown, EntityRef } from '@/lib/drilldown'
import type { Dimension } from '@/lib/filters'

export type { DailyMetrics, Environment, EnvironmentSelector }

// --- provenance ------------------------------------------------------------

/**
 * `not_live`: this section has no real data source yet, so the answer is
 * empty on purpose — not zero, and not an outage (docs/architecture/web-not-live-sections.md).
 */
export type DataSource = 'live' | 'demo' | 'not_live'

export interface PeriodInfo {
  from: string
  to: string
  label: string
  compareLabel: string | null
  previousFrom: string | null
  previousTo: string | null
}

export interface DataMeta {
  source: DataSource
  /** Null when the source does not report one. */
  lastUpdatedAt: string | null
  period?: PeriodInfo
  /**
   * The currency the answer's money is in, when the source names one (the
   * Overview read models report in the tenant's reporting currency). Absent:
   * the tenant's own currency.
   */
  currency?: string
  /** How much of the period the figures can see, for a source that only measures part of it (the funnel). */
  coverage?: MeasurementCoverage
  /** A list read that stopped at its limit: more items matched than it returned. Absent means complete. */
  truncated?: boolean
}

/** `GET /overview/funnel`'s `coverage`: why a funnel is thin or empty, in the API's own words. */
export interface MeasurementCoverage {
  /** A sentence to show beside the figures. */
  note: string
  /** The first measured event in the range; later than the range's start when only part of it is measured. */
  firstEventAt: string | null
  sessionsMeasured: number
  orders: number
  ordersWithSession: number
  /** `ordersWithSession ÷ orders`; null with no orders. */
  ordersWithSessionShare: number | null
}

export interface Envelope<T> {
  meta: DataMeta
  data: T
}

/**
 * True when an answer came from a section that is not live yet: its data is
 * empty on purpose and its mutations are refused. Reads an answer's own
 * provenance (the top bar's badge does); a screen deciding what to render asks
 * `useNotLive(method)` instead, which knows before the answer arrives.
 */
export function isNotLive(meta: Pick<DataMeta, 'source'> | null | undefined): boolean {
  return meta?.source === 'not_live'
}

// --- identity, access and plan --------------------------------------------

/** Mirrors `competition_insights.core.rbac.Permission`. */
export const PERMISSIONS = [
  'analytics.read',
  'exports.create',
  'campaigns.manage',
  'campaigns.send',
  'audiences.manage',
  'advertising.manage',
  'integrations.manage',
  'settings.manage',
  'users.manage',
  'billing.manage',
  'ownership.transfer',
  'diagnostics.read',
  'impersonation.use',
  'customers.reveal',
  'insights.manage',
] as const
export type Permission = (typeof PERMISSIONS)[number]

export function isPermission(value: string): value is Permission {
  return (PERMISSIONS as readonly string[]).includes(value)
}

/** Mirrors `competition_insights.core.rbac.Role`. */
export const ROLE_LABELS: Record<string, string> = {
  owner: 'Owner',
  administrator: 'Administrator',
  marketing_manager: 'Marketing manager',
  marketing_user: 'Marketing user',
  analyst: 'Analyst',
  developer_support: 'Developer support',
  read_only: 'Read only',
}

/** Per-tenant module keys (pivot doc §5, `entitlements`). */
export const MODULE_KEYS = [
  'core_analytics',
  'insights',
  'marketing_intelligence',
  'automation',
  'competition_operations',
  'market_intelligence',
] as const
export type ModuleKey = (typeof MODULE_KEYS)[number]

export interface Entitlements {
  modules: Record<ModuleKey, boolean>
}

export interface TenantSummary {
  id: string
  name: string
  logoUrl: string | null
  /** A brand accent from branding settings; `resolveAccent` decides if it is usable. */
  accentColor: string | null
  currency: string
  locale: string
  /** The tenant's reporting time zone (IANA), when the source gives one: an instant in a fact is written in it. */
  timezone?: string | null
  current: boolean
}

/**
 * TmT support is inside this tenant (blueprint 03 §3.9). Mirrors
 * `modules.auth.schemas.SessionImpersonationOut`: who started it, why, when it
 * ends, and whether it may write. The shell renders it on every page.
 */
export interface Impersonation {
  actor: string
  reason: string
  /** ISO-8601, from the server. The session ends here on its own. */
  expiresAt: string
  elevated: boolean
}

export interface SessionUser {
  /** The signed-in user's own id, so a screen can tell "you" from a colleague. */
  userId: string | null
  name: string | null
  email: string | null
  role: string | null
  roleLabel: string
  permissions: Permission[]
  /** Set while this is a TmT support impersonation, `null` otherwise. */
  impersonation: Impersonation | null
}

export interface HealthStatus {
  status: string
}

// --- overview --------------------------------------------------------------

export const KPI_KEYS = [
  'revenue',
  'orders',
  'tickets',
  'new_customers',
  'returning_customers',
  'signups',
  'aov',
  'conversion_rate',
  'ad_spend',
  'attributed_revenue',
  'roas',
  'active_competitions',
  'automations_running',
] as const
export type KpiKey = (typeof KPI_KEYS)[number]

/** How a KPI tile reads and where it leads: one table for the live client and the demo alike. */
export interface KpiPresentation {
  label: string
  format: ValueFormat
  polarity: Polarity
  drillDown: DrillDown
}

export const KPI_PRESENTATION: Record<KpiKey, KpiPresentation> = {
  revenue: { label: 'Revenue', format: 'currency', polarity: 'up_good', drillDown: { path: '/analytics/revenue' } },
  orders: { label: 'Orders', format: 'integer', polarity: 'up_good', drillDown: { path: '/analytics/revenue', params: { metric: 'orders' } } },
  tickets: { label: 'Tickets / entries', format: 'integer', polarity: 'up_good', drillDown: { path: '/analytics/competitions', params: { metric: 'tickets' } } },
  new_customers: { label: 'New customers', format: 'integer', polarity: 'up_good', drillDown: { path: '/analytics/customers', params: { customer_type: 'new' } } },
  returning_customers: { label: 'Returning customers', format: 'integer', polarity: 'up_good', drillDown: { path: '/analytics/customers', params: { customer_type: 'returning' } } },
  signups: { label: 'Sign-ups', format: 'integer', polarity: 'up_good', drillDown: { path: '/analytics/customers', params: { basis: 'registered' } } },
  aov: { label: 'Average order value', format: 'currency', polarity: 'up_good', drillDown: { path: '/analytics/revenue', params: { metric: 'aov' } } },
  conversion_rate: { label: 'Conversion rate', format: 'percent', polarity: 'up_good', drillDown: { path: '/analytics/funnel' } },
  ad_spend: { label: 'Ad spend', format: 'currency', polarity: 'neutral', drillDown: { path: '/marketing/advertising' } },
  attributed_revenue: { label: 'Attributed ad revenue', format: 'currency', polarity: 'up_good', drillDown: { path: '/marketing/channels' } },
  roas: { label: 'ROAS', format: 'ratio', polarity: 'up_good', drillDown: { path: '/marketing/advertising', params: { metric: 'roas' } } },
  active_competitions: { label: 'Active competitions', format: 'integer', polarity: 'neutral', drillDown: { path: '/analytics/competitions', params: { status: 'active' } } },
  automations_running: { label: 'Automations running', format: 'integer', polarity: 'neutral', drillDown: { path: '/automation/workflows', params: { status: 'running' } } },
}

/** The routes whose daily points can draw a KPI's sparkline in place of the KPI's own. */
export const KPI_SPARKLINE_SOURCE_NAMES = ['signups'] as const
export type KpiSparklineSource = (typeof KPI_SPARKLINE_SOURCE_NAMES)[number]

/**
 * The tiles whose sparkline comes from another route, and which: the Sign-ups tile's is
 * `GET /analytics/signups`' daily points (the Overview README's mapping). Such a KPI
 * carries no sparkline of its own, on the live client and the demo alike.
 */
export const KPI_SPARKLINE_SOURCES: Partial<Record<KpiKey, KpiSparklineSource>> = { signups: 'signups' }

/**
 * Why a KPI has no value yet: it needs consented browser events or a connected
 * ad platform the Overview does not read yet (the API's `not_available`), or
 * no read model serves it at all.
 */
export type KpiUnavailableReason = 'needs_browser_events' | 'needs_ad_platform' | 'no_read_model'

export interface Kpi {
  key: KpiKey
  label: string
  /**
   * Null while the KPI is not available (`unavailable` says why: "Not live
   * yet"), or when the period has none to give (no orders, so no average
   * order value: "—"). Never 0 in its place.
   */
  value: number | null
  /** The comparison period's value; null when comparison is off or undefined. */
  previous: number | null
  /**
   * The change against `previous` as a fraction, when the source computed it
   * (null: no change to show). Absent: derived from `value` and `previous`.
   */
  change?: number | null
  /** Shown in place of the change when there is none, saying why — or why the value itself is none (ROAS with no spend). */
  comparisonNote?: string
  unavailable?: KpiUnavailableReason
  /** A second figure beneath the value: net revenue after refunds beside the gross headline. */
  secondary?: { label: string; value: number }
  format: ValueFormat
  polarity: Polarity
  sparkline: number[]
  drillDown: DrillDown
}

export type TrendWindow = 'today' | '7d' | '30d'

export interface TrendPoint {
  /** An ISO date, or an ISO date and hour for an hourly series. */
  at: string
  label: string
  current: number
  previous: number | null
}

export interface RevenueTrend {
  window: TrendWindow
  /**
   * The API has no hourly buckets, so on the live client `'today'` is one
   * daily point; only the demo spreads today over hours.
   */
  granularity: 'hour' | 'day'
  total: number
  previousTotal: number | null
  compareLabel: string | null
  /** Why there is no comparison although one was asked for: the previous period could not be converted. */
  comparisonNote?: string
  points: TrendPoint[]
  drillDown: DrillDown
}

export const FUNNEL_STAGES = [
  'visitors',
  'competition_viewed',
  'ticket_selector_used',
  'add_to_basket',
  'checkout_started',
  'payment_attempted',
  'purchase_completed',
] as const
export type FunnelStageKey = (typeof FUNNEL_STAGES)[number]

export const FUNNEL_STAGE_LABELS: Record<FunnelStageKey, string> = {
  visitors: 'Visitors',
  competition_viewed: 'Competition viewed',
  ticket_selector_used: 'Ticket selector used',
  add_to_basket: 'Add to basket',
  checkout_started: 'Checkout started',
  payment_attempted: 'Payment attempted',
  purchase_completed: 'Purchase completed',
}

/**
 * One period's figures for a funnel stage, as `GET /overview/funnel` serves
 * them. Rates are fractions, null when their denominator is 0; `dropOff` and
 * `dropOffRate` are null for the first stage, and `dropOff` is negative when
 * more sessions reached this stage than the one before (an unmeasured stage).
 */
export interface FunnelStageFigures {
  /** Distinct users among the stage's sessions. */
  users: number
  /** Measured sessions that reached the stage. */
  sessions: number
  /** `sessions ÷` the previous stage's sessions. */
  stageConversionRate: number | null
  /** `sessions ÷` the first stage's sessions. */
  totalConversionRate: number | null
  /** The previous stage's sessions `−` this stage's. */
  dropOff: number | null
  /** `dropOff ÷` the previous stage's sessions. */
  dropOffRate: number | null
}

export interface FunnelStage extends FunnelStageFigures {
  key: FunnelStageKey
  label: string
  /** The stage's measured sessions (`sessions`), the figure the funnel bars draw. */
  count: number
  previousCount: number | null
  /** The same figures for the comparison period; null with comparison off. */
  previous: FunnelStageFigures | null
  /**
   * False when no event for this stage arrived in the period (the API's
   * `stages_without_events`): shown as "Not measured", never as a 0 % step.
   * Absent means measured.
   */
  measured?: boolean
  drillDown: DrillDown
}

export type PacingStatus = 'ahead' | 'on_track' | 'behind'

/** A null figure is one the source cannot give (shown as "—"), never a zero. */
export interface CompetitionPerformance {
  entity: EntityRef
  category: string | null
  revenue: number
  ticketsSold: number
  /** Purchases ÷ competition views within the period. Not served by the live API yet. */
  conversionRate: number | null
  /** Entries sold to date ÷ maximum entries; null when either is unknown. */
  sellThrough: number | null
  /** Average entries sold per day over the last seven days. Not served by the live API yet. */
  velocityPerDay: number | null
  /** Not served by the live API yet. */
  pacing: PacingStatus | null
  /** The closing day; null when the competition has no end date. */
  closesOn: string | null
  drillDown: DrillDown
}

export type ChannelKind = 'digital' | 'offline'

export interface ChannelPerformance {
  entity: EntityRef
  /** Null only for a campaign channel that has since been deleted: its kind went with it. */
  kind: ChannelKind | null
  /** Null when nothing reports spend for the channel (offline and hand-entered channels): never 0 by default. */
  spend: number | null
  attributedRevenue: number
  orders: number
  cpa: number | null
  roas: number | null
  drillDown: DrillDown
}

export const SEVERITIES = ['critical', 'high', 'medium', 'low'] as const
export type Severity = (typeof SEVERITIES)[number]

export const INSIGHT_CATEGORIES = [
  'revenue',
  'conversion',
  'competition',
  'customer',
  'advertising',
  'payment',
  'technical',
  'market',
] as const
export type InsightCategory = (typeof INSIGHT_CATEGORIES)[number]

/** New until acted on; acknowledging or dismissing is persisted by the API (and kept for the session in the demo). */
export const ALERT_STATES = ['new', 'acknowledged', 'dismissed'] as const
export type AlertState = (typeof ALERT_STATES)[number]

/**
 * The units a fact's value comes in (the API's `insight_writer` facts
 * contract, `Unit`). `money_minor` is whole minor units of the fact's currency;
 * a `fraction` is 0–1.
 */
export type FactUnit =
  | 'money_minor'
  | 'count'
  | 'fraction'
  | 'ratio'
  | 'per_day'
  | 'per_hour'
  | 'hours'
  | 'date'
  | 'datetime'

export type FactConfidence = 'low' | 'medium' | 'high'

/**
 * One **measured** fact an alert was raised on, as the API stores it: the
 * value is an exact decimal string the screen formats but never recomputes.
 * Alerts are a report surface, so an estimate never appears here (ADR-0009
 * D9.11.1): the client drops any fact whose basis is not `measured`.
 */
export interface AlertFact {
  key: string
  /** The facts contract's `FactKind`, e.g. `competition_pacing`. */
  kind: string
  /** The entity's own name; null when it has none. */
  entityName: string | null
  value: string
  unit: FactUnit
  /** Set for money. */
  currency: string | null
  /** ISO dates, inclusive. */
  period: { start: string; end: string }
  comparison: { basis: string; value: string; period: { start: string; end: string } | null } | null
  direction: TrendDirection | null
  confidence: FactConfidence
}

export interface Alert {
  id: string
  title: string
  severity: Severity
  category: InsightCategory
  entity: EntityRef | null
  /** What the alert is about, for display: a competition, a channel, a funnel stage. */
  subject: string
  explanation: string
  occurredAt: string
  drillDown: DrillDown
  /** Where people have taken it, shared by everyone in the tenant. Absent means `'new'`. */
  state?: AlertState
  /** The measured facts it was raised on; empty when the condition carries none. */
  facts: AlertFact[]
}

/** A digest point that explains itself and, where there is somewhere useful to send the reader, links there. */
export interface DigestPoint {
  text: string
  drillDown?: DrillDown
}

export interface DigestAction {
  label: string
  drillDown: DrillDown
}

/** The digest's cadence: yesterday, or the last complete week. */
export const DIGEST_PERIODS = ['daily', 'weekly'] as const
export type DigestPeriod = (typeof DIGEST_PERIODS)[number]

/** The written body of a digest (`insight_writer/render.py`'s `Digest`): every number in it was rendered by the API. */
export interface Digest {
  headline: string
  paragraph: string
  generatedAt: string
  drillDown: DrillDown
  /** Additive digest sections (pivot doc §3a); optional so the one-paragraph summary keeps working without them. */
  whatImproved?: DigestPoint[]
  whatNeedsAttention?: DigestPoint[]
  opportunities?: DigestPoint[]
  recommendedActions?: DigestAction[]
}

/**
 * Who wrote a digest (ADR-0009 D9.7): `claude` is labelled "Written by AI from
 * your figures"; `template` and `fallback_template` (the template standing in
 * when a model write was not possible) are a summary, never called AI.
 */
export type DigestWriter = 'template' | 'claude' | 'fallback_template'

/**
 * One row of the digest's facts drawer: a fact the digest cites, as the API
 * rendered it (`CitedFact`). An `estimate` (D9.11) carries its range and the
 * "estimate" marker in `value` already, with the rule that made it, the
 * measured facts it stands on (`inputs`, their ids) and its assumption.
 */
export interface CitedFact {
  /** The digest's own id for it, e.g. `f3`; `inputs` name these. */
  id: string
  kind: string
  basis: 'measured' | 'estimate'
  name: string
  value: string
  comparison?: string
  direction?: TrendDirection
  confidence: FactConfidence
  periodStart: string
  periodEnd: string
  method?: string
  inputs?: string[]
  assumption?: string
  drillDown: DrillDown
}

export interface DigestVersion {
  version: number
  generatedAt: string
  writer: DigestWriter
}

/** Which digest to read: the latest of a period unless `version` names an earlier one. */
export interface DigestQuery {
  period: DigestPeriod
  version?: number
}

/**
 * `GET /insights/digest`: one period's digest and its versions. With nothing
 * written for the period yet, `digest`, `version`, `writer` and `generatedAt`
 * are null and `notAvailable` says `no_digest_yet`.
 */
export interface DigestReport {
  period: DigestPeriod
  /** ISO dates in the tenant's time zone, inclusive. */
  periodStart: string
  periodEnd: string
  version: number | null
  writer: DigestWriter | null
  generatedAt: string | null
  digest: Digest | null
  facts: CitedFact[]
  /** Every version of the period, newest (the latest) first — the client orders them; the API lists them oldest first. */
  versions: DigestVersion[]
  notAvailable: 'no_digest_yet' | null
}

// --- recommendations and trends ---------------------------------------------

export const RECOMMENDATION_CONFIDENCES = ['low', 'medium', 'high'] as const
export type RecommendationConfidence = (typeof RECOMMENDATION_CONFIDENCES)[number]

/** New until acted on or dismissed; dismissing persists for the mock session. */
export const RECOMMENDATION_STATES = ['new', 'dismissed'] as const
export type RecommendationState = (typeof RECOMMENDATION_STATES)[number]

export interface RecommendationMetric {
  label: string
  value: string
}

export interface RecommendationAction {
  label: string
  drillDown: DrillDown
}

/**
 * A suggested action, never executed by opening it: the action button is a
 * drill-down to where a person approves and carries it out. Nothing in this
 * module calls an automation, campaign or listing API on its own.
 */
export interface Recommendation {
  id: string
  title: string
  category: InsightCategory
  explanation: string
  metrics: RecommendationMetric[]
  estimatedImpact: string
  confidence: RecommendationConfidence
  entity: EntityRef | null
  action: RecommendationAction
  state: RecommendationState
  createdAt: string
}

export const TREND_DIRECTIONS = ['up', 'down', 'flat'] as const
export type TrendDirection = (typeof TREND_DIRECTIONS)[number]

/** Why a trend's latest value is missing (`GET /insights/trends`' `not_available`). */
export type TrendNotAvailable = 'currency_rate_unavailable' | 'no_measured_sessions'

/**
 * A movement over several weeks. Every figure and word here is the API's: the
 * screen shows `changeLabel` and the sparkline as given and never recomputes
 * a change. `direction` and `changeLabel` are null when either week cannot be
 * computed; a sparkline point is null for a week whose value cannot be (never 0).
 */
export interface Trend {
  id: string
  title: string
  category: InsightCategory
  direction: TrendDirection | null
  changeLabel: string | null
  explanation: string
  entity: EntityRef | null
  sparkline: (number | null)[]
  drillDown: DrillDown
  notAvailable?: TrendNotAvailable | null
}

export type AutomationStatus = 'running' | 'paused' | 'draft'

export interface AutomationSummary {
  id: string
  name: string
  status: AutomationStatus
  trigger: string
}

// --- filter vocabulary -----------------------------------------------------

export interface FilterOption {
  value: string
  label: string
  group?: string
}

export type FilterOptions = Record<Dimension, FilterOption[]>

// --- integrations: Meta (wave 7b, docs/architecture/integrations-meta.md) ---

/** No row, a live connection, or one a person has to reconnect. */
export type MetaConnectionStatus = 'not_connected' | 'connected' | 'reconnect_required'

/** An ad account mapped to one of the tenant's websites (`analytics.ad_account`). */
export interface MetaMappedAdAccount {
  adAccountId: string
  /** The Graph node id, `act_<account id>`. */
  externalAdAccountId: string
  name: string | null
  currency: string
  timezone: string | null
  websiteId: string
  environment: Environment
  /** Meta's own account status label (`active`, `disabled`, …). */
  status: string | null
}

/** One provider import, as the Integrations page shows it. */
export interface MetaImportRun {
  startedAt: string
  finishedAt: string | null
  status: MetaImportStatus
  rowsWritten: number
  /** Our own wording of the failure; never the provider's text. Null when it worked. */
  error: string | null
}

export type MetaImportStatus = 'running' | 'succeeded' | 'failed'

/** The newest run per import kind. A kind that has never run is null. */
export interface MetaLastImports {
  /** Campaigns, ad sets, ads and creatives — when the names were last refreshed. */
  hierarchy: MetaImportRun | null
  /** Daily spend and delivery, across the ad accounts that feed a website. */
  insights: MetaImportRun | null
}

/** `GET /integrations/meta`. Never carries the token. */
export interface MetaIntegrationState {
  /** False when this environment cannot connect Meta at all: an operator's fix, not the tenant's. */
  configured: boolean
  missingConfiguration: string[]
  status: MetaConnectionStatus
  connectedUserName: string | null
  scopes: string[]
  tokenExpiresAt: string | null
  connectedAt: string | null
  lastCheckedAt: string | null
  /** Why a person has to reconnect, in words fit to show. */
  lastError: string | null
  tenantCurrency: string
  /** How many days before expiry the daily check re-exchanges the token (`tokens.REFRESH_WINDOW`). */
  refreshWindowDays: number
  adAccounts: MetaMappedAdAccount[]
  lastImports: MetaLastImports
  /**
   * The half-hourly refresh has fallen behind: the connection is fine, but today's
   * spend is not current. False when there is nothing to import — not connected, or
   * no ad account feeds a website yet.
   */
  providerLag: boolean
}

/** An ad account the connected Meta user has a role on. */
export interface MetaAvailableAdAccount {
  externalAdAccountId: string
  accountId: string
  name: string | null
  currency: string
  timezoneName: string | null
  accountStatus: string | null
  /** The website it is mapped to now, if any. */
  websiteId: string | null
  currencyMatchesTenant: boolean
}

export interface MetaAdAccountList {
  items: MetaAvailableAdAccount[]
  tenantCurrency: string
}

export interface MetaAdAccountMapping {
  externalAdAccountId: string
  websiteId: string
}

export interface MetaMappingWarning {
  externalAdAccountId: string
  code: 'currency_mismatch'
  message: string
}

export interface MetaMappingResult {
  items: MetaMappedAdAccount[]
  unmapped: string[]
  warnings: MetaMappingWarning[]
}

// --- Google Analytics 4: `/integrations/ga4` and `/analytics/traffic` (wave 7b) ---

export type Ga4PropertyStatus = 'connected' | 'error' | 'disconnected'
export type Ga4ImportStatus = 'pending' | 'running' | 'succeeded' | 'failed'

/** One connected GA4 property and its import health. */
export interface Ga4Property {
  /** The Suite's id (`ga4p_…`), what disconnect and run-now address. */
  ga4PropertyId: string
  /** GA4's own numeric id. */
  propertyId: string
  displayName: string
  websiteId: string | null
  timezone: string
  currency: string
  status: Ga4PropertyStatus
  connectedAt: string
  lastImportAt: string | null
  lastImportStatus: Ga4ImportStatus
  /** Google's refusal or the import's failure, in words fit to show. */
  lastError: string | null
  /** No successful import for 36 hours. */
  stale: boolean
  /** The daily import missed its slot; the next run catches up. */
  providerLag: boolean
  /** When "Run import now" is accepted again; null when it is accepted now. */
  nextManualImportAt: string | null
  rowsLast30Days: number
}

/** `GET /integrations/ga4`. Never carries the service account's key, only its email. */
export interface Ga4Integration {
  /** False when this environment has no service account at all: an operator's fix. */
  configured: boolean
  /** The address a tenant adds as Viewer on their GA4 property. */
  serviceAccountEmail: string | null
  configurationError: string | null
  properties: Ga4Property[]
}

/** One property a tenant-wide "Run import now" did not run: its own cooldown had not elapsed. */
export interface Ga4ImportSkip {
  ga4PropertyId: string
  retryAfterSeconds: number
}

/**
 * `POST /integrations/ga4/import` (no id): what the tenant-wide run did, and what it
 * skipped. `ran` is the server's own count, not a subtraction: a live property can fail
 * for a reason that is neither a run nor a skip, so deriving it from the other two would
 * be wrong.
 */
export interface Ga4ImportRun {
  integration: Ga4Integration
  ran: number
  skipped: Ga4ImportSkip[]
}

/**
 * `GET /integrations/ga4/properties/{id}/streams`: a read-only, pre-connect check of a
 * property's web-stream hosts against this tenant's website hosts. Never connects or
 * stores anything; the connect route stays the authority on ownership.
 */
export interface Ga4StreamCheck {
  propertyId: string
  streamHosts: string[]
  websiteHosts: string[]
  matches: string[]
  ownershipProvable: boolean
}

export const GA4_GROUP_BY = ['channel', 'source_medium', 'campaign', 'landing', 'device', 'country'] as const
export type Ga4GroupBy = (typeof GA4_GROUP_BY)[number]

/** The one map of group-by labels: the selector, the table's first column and the CSV name read it. */
export const GA4_GROUP_BY_LABELS: Record<Ga4GroupBy, string> = {
  channel: 'Channel',
  source_medium: 'Source / medium',
  campaign: 'Campaign',
  landing: 'Landing page',
  device: 'Device',
  country: 'Country',
}

export interface Ga4TrafficQuery {
  /** Inclusive ISO dates. */
  from: string
  to: string
  groupBy: Ga4GroupBy
  /** Ask for the same number of days immediately before `from`. */
  compare: boolean
  /** One GA4 property; every connected one, summed, when omitted. */
  propertyId?: string
}

export interface Ga4TrafficMetrics {
  sessions: number
  /** GA4 users summed over days: user-days, not distinct people over the period. */
  users: number
  newUsers: number
  engagedSessions: number
  conversions: number
  purchases: number
  /** Minor units of the report's currency; null when the properties summed disagree on it. */
  revenueMinor: number | null
}

export interface Ga4TrafficRow extends Ga4TrafficMetrics {
  key: string
  label: string
  /** The same metrics for the previous period; null when the report was not compared. */
  previous: Ga4TrafficMetrics | null
}

export interface Ga4TrafficReport {
  source: 'ga4'
  start: string
  end: string
  groupBy: Ga4GroupBy
  compared: boolean
  previousStart: string | null
  previousEnd: string | null
  /** Null when the properties summed report in different currencies. */
  currency: string | null
  properties: Array<{ ga4PropertyId: string; propertyId: string; displayName: string; currency: string }>
  rows: Ga4TrafficRow[]
  totals: Ga4TrafficMetrics
  totalsPrevious: Ga4TrafficMetrics | null
  /** How current the sum is: the oldest last import among its properties. */
  freshness: { lastImportAt: string | null; stale: boolean }
}

// --- Meta advertising campaign report (wave 7b, docs/architecture/integrations-meta.md §12) ---

/** The three 05 §5.8 attribution models and paid click (the latest paid click in Meta's
 * 7-day click window, else the last non-direct visit — the Meta and ads reads' default),
 * in the API enum's order — the Ads page's own model selector (M2,
 * docs/architecture/integrations-meta.md §12.6) reads and writes this. */
export const ATTRIBUTION_MODELS = ['first_touch', 'last_touch', 'last_non_direct', 'paid_click'] as const
export type AttributionModel = (typeof ATTRIBUTION_MODELS)[number]

export const ATTRIBUTION_MODEL_LABELS: Record<AttributionModel, string> = {
  first_touch: 'First touch',
  last_touch: 'Last touch',
  last_non_direct: 'Last non-direct',
  paid_click: 'Paid click',
}

export const ADVERTISING_LEVELS = ['campaign', 'ad_set', 'ad'] as const
export type AdvertisingLevel = (typeof ADVERTISING_LEVELS)[number]

export const ADVERTISING_LEVEL_LABELS: Record<AdvertisingLevel, string> = {
  campaign: 'Campaign',
  ad_set: 'Ad set',
  ad: 'Ad',
}

/** The next level a row of `level` drills down into, or `null` at the bottom. */
export const ADVERTISING_CHILD_LEVEL: Record<AdvertisingLevel, AdvertisingLevel | null> = {
  campaign: 'ad_set',
  ad_set: 'ad',
  ad: null,
}

export interface AdvertisingQuery {
  /** Inclusive ISO dates. */
  from: string
  to: string
  compare: boolean
  level: AdvertisingLevel
  /** A campaign id at `level: 'ad_set'`, an ad set id at `level: 'ad'`. */
  parentId?: string
  q?: string
  /** A metric name or `'name'`, `-` prefixed for descending. */
  sort?: string
  /** Paging over the matching rows; `totals`/`totalsPrevious` cover every matching row regardless. */
  limit?: number
  offset?: number
  /** The model the TmT-attributed columns are computed under. Default `paid_click` (the Meta reads' default). */
  model?: AttributionModel
}

export interface AdvertisingMetrics {
  spendMinor: number
  impressions: number
  clicks: number
  landingPageViews: number
  /** `clicks / impressions`; null when there were no impressions. */
  ctr: number | null
  /** `spendMinor / clicks`; null when there were no clicks. */
  cpcMinor: number | null
  /** Spend per 1,000 impressions; null when there were no impressions. */
  cpmMinor: number | null
  /** Provider-reported cross-surface purchases. Never TmT's own. */
  providerPurchases: number
  /** Provider-reported revenue of those purchases, in the report's currency. */
  providerPurchaseRevenueMinor: number
  /** `spendMinor / providerPurchases`; null when there were no purchases. */
  providerCpaMinor: number | null
  /** `providerPurchaseRevenueMinor / spendMinor`; null when there was no spend. */
  providerRoas: number | null
}

/** The metric keys, in the order the table and the CSV show them. */
export const ADVERTISING_METRIC_KEYS = [
  'spendMinor',
  'impressions',
  'clicks',
  'landingPageViews',
  'ctr',
  'cpcMinor',
  'cpmMinor',
  'providerPurchases',
  'providerPurchaseRevenueMinor',
  'providerCpaMinor',
  'providerRoas',
] as const satisfies readonly (keyof AdvertisingMetrics)[]

/** TmT's own attributed figures for one row (or the report's totals), under
 * `model`: never Meta's own count, and never folded into `AdvertisingMetrics`
 * (06 §6.10). Ratios are the period's summed purchases/revenue/new customers
 * against that same row's own (period) spend — never an average of daily ratios —
 * and are null when their denominator is zero. */
export interface TmtAttributedMetrics {
  model: AttributionModel
  tmtAttributed: true
  purchases: number
  revenueMinor: number
  newCustomers: number
  /** `spendMinor / purchases`; null when there were no purchases. */
  cpaMinor: number | null
  /** `revenueMinor / spendMinor`; null when there was no spend. */
  roas: number | null
  /** `spendMinor / newCustomers`; null when there were no new customers. */
  costPerNewCustomerMinor: number | null
}

/** The TmT metric keys, in the order the table and the CSV show them after the
 * provider columns. */
export const TMT_ATTRIBUTED_METRIC_KEYS = [
  'purchases',
  'revenueMinor',
  'newCustomers',
  'cpaMinor',
  'roas',
  'costPerNewCustomerMinor',
] as const satisfies readonly (keyof Omit<TmtAttributedMetrics, 'model' | 'tmtAttributed'>)[]

/** TmT-attributed purchases whose credited touch was paid but resolved to no
 * imported Meta ad — a data-quality figure, independent of `level`/`parentId`. */
export interface TmtUnmatchedPaid {
  purchases: number
  revenueMinor: number
}

export interface AdvertisingRow {
  id: string
  name: string | null
  status: string | null
  /** The status the provider derives from the parents and the review, e.g. a paused campaign's ad sets read `campaign_paused`. */
  effectiveStatus: string | null
  metrics: AdvertisingMetrics
  /** The same metrics for the compared period; null when the report was not compared. */
  previous: AdvertisingMetrics | null
  /** TmT's own figures for this row, LEFT-joined onto it: an object with delivery
   * and no TmT purchases shows zeros. */
  tmt: TmtAttributedMetrics
  tmtPrevious: TmtAttributedMetrics | null
}

/** When this answer was computed, and how current it is — the Integrations page's own rule (`health.provider_lag`). */
export interface AdvertisingFreshness {
  generatedAt: string
  lastRecentImport: MetaImportRun | null
  providerLag: boolean
  /** A live Meta connection exists. */
  connected: boolean
  /** How many ad accounts feed a website. */
  mappedAccounts: number
}

export interface AdvertisingReport {
  source: 'meta'
  start: string
  end: string
  level: AdvertisingLevel
  parentId: string | null
  compared: boolean
  previousStart: string | null
  previousEnd: string | null
  /** Every row and total is in this currency: the tenant's. */
  currency: string
  /** The distinct attribution windows behind the purchases and revenue in this answer. */
  attributionWindows: string[]
  rows: AdvertisingRow[]
  totals: AdvertisingMetrics
  totalsPrevious: AdvertisingMetrics | null
  /** The attribution model every `tmt` block on this answer was computed under. */
  tmtModel: AttributionModel
  /** How far before an order a touch may be credited — the TmT badge's tooltip. */
  tmtLookbackDays: number
  tmtTotals: TmtAttributedMetrics
  tmtTotalsPrevious: TmtAttributedMetrics | null
  tmtUnmatchedPaid: TmtUnmatchedPaid
  asOf: AdvertisingFreshness
}

/** The metric keys, in the order tables and files show them: the one list the table, the totals, the demo and its CSV derive from. */
export const GA4_METRIC_KEYS = [
  'sessions',
  'users',
  'newUsers',
  'engagedSessions',
  'conversions',
  'purchases',
  'revenueMinor',
] as const satisfies readonly (keyof Ga4TrafficMetrics)[]

/**
 * The metric keys as a type. Its default type argument is the compile-time
 * check that `GA4_METRIC_KEYS` names every `Ga4TrafficMetrics` field: add
 * `foo: number` to the interface without adding `'foo'` to the list and this
 * line fails with "Type 'false' does not satisfy the constraint 'true'".
 * Types only: nothing is exported or emitted for the check.
 */
export type Ga4MetricKey<Complete extends true = MetricKeysComplete> = Complete extends true ? (typeof GA4_METRIC_KEYS)[number] : never

type MetricKeysComplete = [Exclude<keyof Ga4TrafficMetrics, (typeof GA4_METRIC_KEYS)[number]>] extends [never] ? true : false

// --- connector health (wave 9: cards 3e6c00e6…3c4d, 3e7c00e6…1c08c7) ---------

/** The one word on a website's health card; the API's rule (`modules/connector_health`) decides it. */
export const WEBSITE_HEALTH_STATUSES = ['connected', 'no_events_yet', 'stale', 'needs_attention'] as const
export type WebsiteHealthStatus = (typeof WEBSITE_HEALTH_STATUSES)[number]

/** What a website's connector last reported about its reconcile runs and its retry queue. */
export interface ConnectorHealthReport {
  /** When the Suite received it: the Suite's clock, not the connector's. */
  reportedAt: string
  connectorVersion: string
  contractVersion: string
  lastReconcileStartedAt: string | null
  lastReconcileFinishedAt: string | null
  lastReconcileWindowFrom: string | null
  lastReconcileWindowTo: string | null
  lastReconcileSent: number
  lastReconcileSkipped: number
  lastReconcileFailed: number
  lastReconcileErrorClass: string | null
  nextRunDueAt: string | null
  missedRun: boolean
  queueBacklog: number
  queueParked: number
  queueOldestAgeSeconds: number | null
}

/** `GET /integrations/websites/{id}/health`: is this website's connector delivering? */
export interface WebsiteHealth {
  websiteId: string
  environment: Environment
  status: WebsiteHealthStatus
  /** The newest event received per group in the last 7 days; null for none. */
  lastEventAt: { pageViews: string | null; commerce: string | null; entities: string | null }
  quarantinedLast24h: number
  report: ConnectorHealthReport | null
  /** When the event figures were read; the API reuses them for up to a minute. */
  computedAt: string
}
